import { Clickable, Col, Divider, Icon, Row, Sidepanel, Spinner2, Text } from 'components';
import { theme } from 'lib';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import QuizCard from './QuizCard';

const SCROLL_ID = 'quick-quiz-list-scroll';

const Empty = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${theme.GREY_60};
`;

// Dedicated close button — 36px circular hit target with a hover tint, so the
// × is unmissable and easy to tap on touch devices.
const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${theme.GREY_10};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover {
    background: ${theme.GREY_20};
  }
`;

// Each row stretches to the panel width so the card fills the column instead
// of staying at its 296px design width. QuizCard's width is fixed for the
// horizontal row use case — we override it here for the vertical list.
const FullWidthSlot = styled.div`
  width: 100%;
  & > button,
  & > div {
    width: 100%;
    min-width: 0;
  }
`;

const Footer = styled.div`
  padding: 16px 0 8px;
  display: flex;
  justify-content: center;
  color: ${theme.GREY_60};
  font-size: 12px;
`;

// 1px invisible sentinel at the bottom of the scrollable region. When it
// scrolls into view we ask the parent for the next page. IntersectionObserver
// handles scroll events natively and rebinds cleanly on prop changes — much
// more reliable than hooking into Sidepanel's once-mount scroll listener,
// which captures a stale `onContentEndReached` closure if hasMore/onLoadMore
// flips after first mount.
const Sentinel = styled.div`
  width: 100%;
  height: 1px;
`;

/**
 * QuizListPanel — right-side Sidepanel listing every quiz for this team.
 *
 * Opened from the profile row's "See all" link. Reuses QuizCard with a
 * full-width override so admins can manage from the same surface (edit /
 * delete / toggle) without leaving the profile.
 */
const QuizListPanel = ({
  visible,
  quizzes,
  loading,
  hasMore,
  onLoadMore,
  // Per-quiz permission predicate. Half-time quizzes need P4P admin even when
  // the viewer is the team owner — see canManageQuiz in the parent row.
  canManageQuiz,
  onClose,
  onPlay,
  onEdit,
  onDelete,
  onToggleActive
}) => {
  const { t } = useTranslation();
  const sentinelRef = useRef(null);

  // Infinite scroll via IntersectionObserver. Root is the Sidepanel's
  // scrollable container (Sidepanel mounts a <div id={contentId}> for us).
  // rootMargin: '200px' so we trigger a fetch before the user hits the
  // absolute bottom — keeps the feed feeling continuous.
  useEffect(() => {
    if (!visible || !hasMore || !sentinelRef.current) return;
    const root = document.getElementById(SCROLL_ID);
    if (!root) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          onLoadMore && onLoadMore();
        }
      },
      { root, rootMargin: '200px' }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [visible, hasMore, onLoadMore, quizzes.length]);

  return (
    <Sidepanel
      visible={visible}
      wid={520}
      pad="0"
      closeModal={() => {}}
      contentId={SCROLL_ID}
    >
      {/* Custom header — ContentHeader uses a left-chevron back arrow which
          reads as "go back" rather than "close this panel". A right-aligned
          × is the standard close affordance for a sidepanel. */}
      <Col pos="sticky" bg="#fff" top="0" zIndex={2} wid="100%">
        <Row between center wid="100%" pad="14px 20px">
          <Text h3>{t('pollQuizProfile.allQuizzes')}</Text>
          <CloseBtn type="button" aria-label={t('pollQuizProfile.close')} onClick={onClose}>
            <Icon type="close" dimensions={14} col={theme.GREY_80} />
          </CloseBtn>
        </Row>
        <Divider />
      </Col>

      <Col wid="100%" pad="16px 20px 24px" gap="12px">
        {loading && quizzes.length === 0 ? (
          <Col centerAll pad="40px 0">
            <Spinner2 col={theme.SECONDARY} />
          </Col>
        ) : quizzes.length === 0 ? (
          <Empty>
            <Text col={theme.GREY_60}>{t('pollQuizProfile.noQuizzesYet')}</Text>
          </Empty>
        ) : (
          <>
            {quizzes.map((q) => (
              <FullWidthSlot key={q._id}>
                <QuizCard
                  quiz={q}
                  isMobile={false}
                  canManage={canManageQuiz ? canManageQuiz(q) : false}
                  onPlay={onPlay}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onToggleActive={onToggleActive}
                />
              </FullWidthSlot>
            ))}

            {/* Sentinel — the IntersectionObserver above watches this element.
                Sits just above the footer so the trigger fires slightly before
                the user can see the spinner. */}
            <Sentinel ref={sentinelRef} />

            {hasMore ? (
              <Footer>
                <Spinner2 col={theme.SECONDARY} />
              </Footer>
            ) : (
              quizzes.length > 0 && <Footer>{t('pollQuizProfile.seenThemAll')}</Footer>
            )}
          </>
        )}
      </Col>
    </Sidepanel>
  );
};

export default QuizListPanel;
